import React from 'react';
import { useNavigate } from 'react-router-dom';
import { UserAvatar, useTranslations } from 'react-ui';
import { usePartnerScope } from '../contexts/PartnerScope';
import { useBalloonEventsData } from '../contexts/EventData';

/**
 * Left-pane header shown when the map is in partner-scoped mode (`/partner/:code`).
 * Renders a "back to all events" CTA, the partner's logo + name, and a
 * "View partner details" link that is active when the pane is closed and
 * greyed-out when it is already open. Mirrors UserScopeHeader.
 */
export const PartnerScopeHeader: React.FC = () => {
    const { code, profile, hasEvents, paneOpen, openProfilePane } = usePartnerScope();
    const { deselectEvent } = useBalloonEventsData();
    const { translated } = useTranslations();
    const navigate = useNavigate();

    // Partners without public events don't scope the list — the generic list
    // keeps its normal header; only the detail pane slides in.
    if (!code || !hasEvents) return null;

    const name = profile?.name ?? code;

    const handleViewProfile = () => {
        deselectEvent();
        openProfilePane();
    };

    return (
        <div className="partner-scope-header px-4 pt-3 pb-3 border-b border-gray-100">
            <button
                type="button"
                onClick={() => navigate('/')}
                className="text-sm text-gray-500 hover:text-gray-800 transition-colors flex items-center gap-1 mb-3"
            >
                <span aria-hidden>‹</span> {translated('all_events') || 'All events'}
            </button>

            <div className="flex items-center gap-3">
                <UserAvatar
                    name={name}
                    mainImage={profile?.logo}
                    media={profile?.media}
                    size={40}
                    className="flex-shrink-0"
                />

                <div className="min-w-0 flex-1">
                    <p className="font-semibold text-gray-900 truncate">{name}</p>
                    {paneOpen ? (
                        <span className="text-xs text-gray-400">{translated('viewing_details') || 'Viewing details'}</span>
                    ) : (
                        <button
                            type="button"
                            onClick={handleViewProfile}
                            className="text-xs text-balloon hover:underline text-left"
                        >
                            {translated('view_partner_details') || 'View partner details'}
                        </button>
                    )}
                </div>
            </div>
        </div>
    );
};

export default PartnerScopeHeader;
